import { FileIconType } from '@digitale-menschen/component-library';
import { LexplusUploadDateiTypEnum } from '@lexplus-user-api';

/**
 * How one file type is presented. `dm-icon-file` covers the seven real formats; the two
 * remaining enum values have no glyph in that set and fall back to a `dm-icon`:
 *
 * - `link` is not a file at all — an external reference, which the design draws as a chain
 *   link rather than a page.
 * - `sonstig` is the catch-all, drawn as the neutral `document` page.
 */
export interface UploadDateiTypDefinition {
  value: LexplusUploadDateiTypEnum;
  /** The `dm-icon-file` type, or `null` when the type is drawn with a `dm-icon` instead. */
  iconFileType: FileIconType | null;
  /** The `dm-icon` name used when {@link iconFileType} is `null`. */
  iconName: string | null;
  /** The label in the file-type filter and as the icon's accessible name. */
  label: string;
}

/**
 * The file types in the order the filter offers them: documents first, then media, then
 * the two that are not really formats.
 */
export const UPLOAD_DATEI_TYPEN: readonly UploadDateiTypDefinition[] = [
  { value: LexplusUploadDateiTypEnum.Pdf, iconFileType: 'pdf', iconName: null, label: 'PDF' },
  { value: LexplusUploadDateiTypEnum.Docx, iconFileType: 'docx', iconName: null, label: 'DOCX' },
  { value: LexplusUploadDateiTypEnum.Xlsx, iconFileType: 'xlsx', iconName: null, label: 'XLSX' },
  { value: LexplusUploadDateiTypEnum.Pptx, iconFileType: 'pptx', iconName: null, label: 'PPTX' },
  { value: LexplusUploadDateiTypEnum.Jpeg, iconFileType: 'jpeg', iconName: null, label: 'JPEG' },
  { value: LexplusUploadDateiTypEnum.Png, iconFileType: 'png', iconName: null, label: 'PNG' },
  { value: LexplusUploadDateiTypEnum.Mp4, iconFileType: 'mp4', iconName: null, label: 'MP4' },
  {
    value: LexplusUploadDateiTypEnum.Link,
    iconFileType: null,
    iconName: 'link-03',
    label: $localize`:@@dokumente.dateiTyp.link:Verlinkung`,
  },
  {
    value: LexplusUploadDateiTypEnum.Sonstig,
    iconFileType: 'document',
    iconName: null,
    label: $localize`:@@dokumente.dateiTyp.sonstig:Sonstige`,
  },
];

const UPLOAD_DATEI_TYPEN_BY_VALUE = new Map<string, UploadDateiTypDefinition>(
  UPLOAD_DATEI_TYPEN.map((definition) => [definition.value, definition]),
);

/** The catch-all, used for a type the client does not know because the API added one. */
const FALLBACK_DEFINITION: UploadDateiTypDefinition = UPLOAD_DATEI_TYPEN[UPLOAD_DATEI_TYPEN.length - 1];

/**
 * How a file type is presented. Takes the plain string because the user and admin API clients each
 * generate their own, mutually unassignable, copy of the enum.
 *
 * @param {string} dateiTyp - The type the API reports
 * @returns {UploadDateiTypDefinition} Its presentation, falling back to «Sonstige»
 */
export function resolveUploadDateiTyp(dateiTyp: string): UploadDateiTypDefinition {
  return UPLOAD_DATEI_TYPEN_BY_VALUE.get(dateiTyp) ?? FALLBACK_DEFINITION;
}

/** The types whose bytes the drawer can show inline; everything else offers a download only. */
const VORSCHAU_TYPEN: readonly string[] = [
  LexplusUploadDateiTypEnum.Pdf,
  LexplusUploadDateiTypEnum.Jpeg,
  LexplusUploadDateiTypEnum.Png,
  LexplusUploadDateiTypEnum.Mp4,
];

/**
 * Whether the drawer renders a preview of this type rather than a placeholder. The server
 * decides the same thing per upload (`vorschauMoeglich`); this answers it for a type alone,
 * where no upload is at hand — the file-type filter's hint, for instance.
 *
 * @param {string} dateiTyp - The type the API reports
 * @returns {boolean} Whether a preview exists for it
 */
export function hasInlineVorschau(dateiTyp: string): boolean {
  return VORSCHAU_TYPEN.includes(dateiTyp);
}
